/** @jsxImportSource @emotion/react */
import React, { useCallback, useRef, useEffect } from 'react';
import { css } from '@emotion/react';

import { useAssetImageQuery } from '../../../actions/FolderActions';
import { useTranslation } from 'react-i18next';

import { Link, useNavigate } from 'react-router-dom';
import { useIntersection, useUnmount } from 'react-use';
import { useQueryClient } from 'react-query';

import { useMeetingActions } from '../../../actions/MeetingActions';
import { useFileActions } from '../../../actions/FileActions';
import { useDocumentActions } from '../../../actions/DocumentActions';

import { confirmDialog } from '../../../utils/dialog';
import { useIsMobile } from '../../../utils/hooks';

import Text from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import MeetingStatus from '../../meetings/MeetingStatus';
import AssetSnapshot from './AssetSnapshot';
import SourceIcon from '../SourceIcon';

import MoreMenu from './MoreMenu';

function noop() {}

export default function Asset({
  compact,
  singleSelect,
  name,
  source,
  file,
  type,
  id,
  uploading,
  progress,
  isModal,
  selected,
  onSelectionChange,
  mimetype,
  deletePermission,
  onShowExport,
  hideOptions = false,
  width = 239,
  onClick = noop,
  userFallbackFolder = false,
  disabled = false,
  ...asset
}) {
  const { i18n } = useTranslation();
  const { isMobile } = useIsMobile();
  const link = type
    ? `/${type === 'meetings' ? 'transcript' : type}/${id}`
    : file
    ? `/file/${id}`
    : `/transcript/${id}`;
  const modalWidth = width - 16 - 40;

  const ref = useRef(null);
  const obs = useIntersection(ref, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  });
  const isInViewport = obs?.isIntersecting;

  const queryClient = useQueryClient();
  const { image, cacheKey } = useAssetImageQuery(id, file, {
    enabled:
      !!isInViewport &&
      !!id &&
      ((!compact && !!file) || (!file && asset?.media_type === 'video')),
    width: isModal ? modalWidth : width - 16,
  });

  useUnmount(() => {
    queryClient.cancelQueries(cacheKey);
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleEdit = useCallback(() => {
    navigate(link);
  }, [navigate, link]);

  const {
    deleteMeetingAsync,
    isDeleting: isDeletingMeeting,
    updateMeeting,
  } = useMeetingActions(id);
  const {
    deleteFileAsync,
    isDeleting: isDeletingFile,
    updateFile,
  } = useFileActions(id);

  const {
    deleteDocumentAsync,
    isDeleting: isDeletingDocument,
    updateDocument,
  } = useDocumentActions(undefined, id);

  const handleDelete = useCallback(async () => {
    const confirm = await confirmDialog({
      title: t('Ta bort fil'),
      content: t('Är du säker på att du vill ta bort filen?'),
    });

    if (confirm) {
      let _type = type || (file ? 'file' : 'transcript');
      if (_type === 'file') {
        await deleteFileAsync();
      } else if (_type === 'transcript') {
        await deleteMeetingAsync();
      } else if (_type === 'document') {
        await deleteDocumentAsync();
      }
    }
  }, [file, deleteFileAsync, deleteMeetingAsync, deleteDocumentAsync, t]);

  const handleMove = useCallback(
    ({ folder_prefix, old_folder_prefix }) => {
      if (type === 'document') {
        updateDocument({
          folder_prefix,
        });
      } else if (type === 'file') {
        updateFile({
          folder_prefix,
        });
      } else {
        updateMeeting({
          folder_prefix,
          old_folder_prefix,
        });
      }
    },
    [file, updateFile, updateMeeting]
  );

  if (compact) {
    return (
      <TableRow
        css={css`
          pointer-events: ${disabled ? 'none' : 'auto'};
          opacity: ${disabled ? 0.6 : 1};
          cursor: pointer;
          text-decoration: none;
          color: var(--text-primary);
          background-color: ${selected ? 'var(--primary-50)' : 'inherit'};
        `}
        onClick={(e) => {
          if (disabled) return;
          if (isModal) {
            e.stopPropagation();
            if (typeof onSelectionChange === 'function') {
              onSelectionChange({
                assetId: id,
                type: type || (file ? 'file' : 'transcript'),
              });
            }
          }
        }}
      >
        <TableCell ref={ref}>
          <Link
            to={isModal ? '#' : link}
            css={css`
              cursor: pointer;
              text-decoration: none;
              color: var(--text-primary);
              display: flex;
              align-items: center;
              width: 100%;
            `}
          >
            {isModal && !singleSelect && !disabled ? (
              <Checkbox
                disabled={disabled}
                css={css`
                  padding: 0px;
                `}
                checked={selected}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  if (typeof onSelectionChange === 'function') {
                    onSelectionChange({
                      assetId: id,
                      type: type || (file ? 'file' : 'transcript'),
                    });
                  }
                }}
              />
            ) : null}
            {image ? (
              <img
                src={image}
                alt={name}
                css={css`
                  max-width: 24px;
                  max-height: 24px;
                  width: auto;
                  height: auto;
                `}
              />
            ) : (
              <SourceIcon source={source} mimetype={mimetype} />
            )}
            <Text
              variant="subtitle2"
              fontWeight="600"
              css={css`
                margin-left: 8px;
                max-width: 22vw;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {name}
            </Text>
          </Link>
        </TableCell>
        <TableCell>
          {asset?.status && <MeetingStatus status={asset?.status} />}
        </TableCell>
        <TableCell>
          {uploading ? (
            <LinearProgress
              variant="determinate"
              value={100 * progress}
              css={css`
                width: 100%;
              `}
            />
          ) : (
            new Date(asset.updated_at).toLocaleDateString(i18n.language)
          )}
        </TableCell>
        {!isModal ? (
          <TableCell
            css={css`
              text-align: right;
            `}
          >
            {isDeletingMeeting || isDeletingFile ? (
              <CircularProgress size={20} />
            ) : hideOptions ? null : (
              <MoreMenu
                deletePermission={deletePermission}
                download={!file}
                onShowExport={(() => onShowExport({ id, name })) || noop}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMove={handleMove}
                folderPrefix={asset.folder_prefix}
                allowMoveBetweenProjects={true}
                isCompact
                disableDownload={uploading || asset?.status !== 'ready'}
              />
            )}
          </TableCell>
        ) : null}
      </TableRow>
    );
  }

  return (
    <div
      to={!uploading && !isModal ? link : '#'}
      disabled={uploading}
      css={css`
        display: flex;
        gap: 16px;
        width: ${width}px;
        min-width: ${width}px;
        min-height: 180px;
        overflow-wrap: anywhere;
      `}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        if (typeof onSelectionChange === 'function') {
          onSelectionChange({
            assetId: id,
            type: type || (file ? 'file' : 'transcript'),
          });
        }
        onClick();
      }}
      ref={ref}
    >
      {isModal && !singleSelect && !disabled ? (
        <Checkbox
          css={css`
            padding: 0px;
          `}
          disabled={disabled}
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            if (typeof onSelectionChange === 'function') {
              onSelectionChange({
                assetId: id,
                type: type || (file ? 'file' : 'transcript'),
              });
            }
          }}
          disableRipple
        />
      ) : null}
      <div
        css={css`
          pointer-events: ${disabled ? 'none' : 'auto'};
          opacity: ${disabled ? 0.6 : 1};
          border-radius: 8px;
          background-color: #eeeeee;
          padding: 0.5rem;
          flex-grow: 1;
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-direction: column;
          border: 2px solid transparent;
          ${selected && singleSelect
            ? 'border: 2px solid var(--primary-main);'
            : ''}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
            width: 100%;
          `}
        >
          <Link
            to={!uploading && !isModal ? link : '#'}
            css={css`
              cursor: ${uploading ? 'not-allowed' : 'pointer'};
              text-decoration: none;
              color: var(--text-primary);

              display: flex;
              flex-direction: row;
              gap: 0.5rem;
              align-items: center;
              width: 100%;
              max-width: ${isModal ? `${modalWidth}px` : `${width}px`};
            `}
          >
            <div>
              <SourceIcon source={source} mimetype={mimetype} />
            </div>
            <Tooltip title={name} enterDelay={500}>
              <Text
                variant="subtitle2"
                css={css`
                  max-width: ${isMobile ? '64px' : isModal ? '128px' : '148px'};
                  text-overflow: ellipsis;
                  overflow: hidden;
                  white-space: nowrap;
                `}
                fontWeight="600"
              >
                {name}
              </Text>
            </Tooltip>
          </Link>
          {!hideOptions ? (
            <div
              css={css`
                padding-top: 0.125rem;
              `}
            >
              <MoreMenu
                deletePermission={deletePermission}
                download={!file && !type}
                onShowExport={(() => onShowExport({ id, name })) || noop}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMove={handleMove}
                folderPrefix={asset.folder_prefix}
                allowMoveBetweenProjects={true}
                disableDownload={uploading || asset?.status !== 'ready'}
              />
            </div>
          ) : null}
        </div>
        <Link
          to={!uploading && !isModal ? link : '#'}
          css={css`
            cursor: ${uploading ? 'not-allowed' : 'pointer'};
            text-decoration: none;
            color: var(--text-primary);
            height: 100%;
            height: 150px;
            width: 100%;
            display: flex;
            justify-content: center;
          `}
        >
          <AssetSnapshot
            id={id}
            name={name}
            image={image}
            {...asset}
            mimetype={mimetype}
            mediaType={asset?.media_type}
            maxWidth={isModal ? modalWidth - 16 : width - 16}
            type={type || (file ? 'asset' : 'transcript')}
            isInViewport={isInViewport}
          />
        </Link>
        {uploading && progress < 1.0 ? (
          <LinearProgress
            variant="determinate"
            value={98 * progress}
            css={css`
              width: 100%;
            `}
          />
        ) : null}
      </div>
    </div>
  );
}
